import { StorageAdapter } from '../storage/StorageAdapter'
import { BaseRepository } from './BaseRepository'
import { newId } from './ids'

/**
 * AnswersRepository — el repo critico de IDEMPOTENCIA (CAP-03). Re-implementacion
 * limpia (SQL) del flujo de respuestas de v1 (REFERENCIA — NO importado).
 *
 * Tabla `answers`, entity `occurrence` (por contrato), pk `client_uuid`,
 * conflictTarget 'occurrence_id, item_id'.
 *
 * Idempotencia: enforced en SQL por UNIQUE(occurrence_id, item_id) (schema.js:46).
 * El repo garantiza un client_uuid ESTABLE por (occurrence_id, item_id) para que el
 * coalesce-por-client_uuid del outbox sea correcto entre re-ediciones.
 *
 * Reentrancy-safe (Open Q3): TODO el flujo corre dentro de UNA sola
 * StorageAdapter.transaction(fn) y delega en BaseRepository._saveOptimisticTx con
 * db.run(...,false) — NUNCA llama saveOptimistic() (abriria una 2a tx -> doble-BEGIN)
 * NI executeSet() dentro de esa transaccion.
 *
 * Bodies NORMALIZADOS (F3 construye el REST). Latest-state -> coalesce.
 */
export class AnswersRepository extends BaseRepository {
  constructor () {
    super('answers', 'occurrence', 'client_uuid')
  }

  /**
   * Resuelve el client_uuid ESTABLE de una respuesta por (occurrence_id, item_id): el existente si la
   * fila ya existe, o uno DETERMINISTA `ans:<occ>:<item>` si es nueva. Permite a la fachada conocer el
   * uuid ANTES de capturar la foto, así el parent_uuid del photo_meta nace ya apuntando al client_uuid
   * del op (sin reparent post-save).
   *
   * Por qué DETERMINISTA y no newId(): si dos saveAnswer concurrentes del MISMO (occ,item) con foto
   * leyeran "no hay prior" antes de que cualquiera commitee, con newId() cada uno generaría un uuid
   * distinto -> la 2ª foto nacería con un parent_uuid que ningún op del outbox tiene (el outbox coalesce
   * bajo el 1º) -> binario huérfano + falso positivo photo_orphan. La clave determinista garantiza que
   * ambos resuelvan el MISMO uuid sin depender de una lectura con carrera. El client_uuid es solo una
   * clave LOCAL de coalesce (el server no la ve), así que el formato es libre. Las answers EXISTENTES
   * conservan su uuid previo (el SELECT gana) -> sin cambio para datos ya guardados.
   * @returns {Promise<string>}
   */
  async stableClientUuid (occurrenceId, itemId) {
    const r = await StorageAdapter.query(
      'SELECT client_uuid FROM answers WHERE occurrence_id=? AND item_id=?',
      [occurrenceId, itemId]
    )
    const row = r && r.values && r.values[0]
    return (row && row.client_uuid) || `ans:${occurrenceId}:${itemId}`
  }

  /**
   * Guarda/actualiza una respuesta de forma idempotente por (occurrence_id, item_id).
   * @param {object} ans  (client_uuid opcional: si la fachada ya lo pre-resolvió, se usa cuando no hay prior)
   * @returns {Promise<string>} client_uuid estable de la respuesta.
   */
  saveAnswer (fields = {}) {
    return StorageAdapter.transaction(db => this.saveAnswerIn(db, fields))
  }

  /** Igual que saveAnswer, dentro de una transacción ya abierta (`db` es su handle). */
  async saveAnswerIn (db, {
    occurrence_id: occurrenceId,
    item_id: itemId,
    value,
    current_points: currentPoints,
    total_points: totalPoints,
    extra_properties: extraProperties,
    image_ref: imageRef,
    local_path: localPath,
    client_uuid: providedUuid,
    created_at: providedCreatedAt,
    updated_at: providedUpdatedAt
  } = {}) {
    // 1) Resuelve un client_uuid ESTABLE para (occurrence_id, item_id) (Open Q2):
    //    reusa el existente si lo hay; si no, genera uno nuevo. Asi el coalesce por
    //    client_uuid es correcto entre re-ediciones de la misma respuesta.
    const existing = await db.query(
      'SELECT client_uuid, created_at FROM answers WHERE occurrence_id=? AND item_id=?',
      [occurrenceId, itemId]
    )
    const prior = existing && existing.values && existing.values[0]
    // prior (re-edición) gana; si no hay, usa el uuid pre-resuelto por la fachada (mismo que sembró
    // el parent_uuid de la foto); si tampoco, uno nuevo. Idempotencia intacta (UNIQUE occ+item).
    const uuid = (prior && prior.client_uuid) || providedUuid || newId()
    const now = new Date().toISOString()
    // created_at: gana el existente (sticky), luego el provisto (migración v1 -> conserva la hora real
    // de captura en vez de sembrar now()), luego now(). updated_at: el provisto (migración) o now().
    const createdAt = (prior && prior.created_at) || providedCreatedAt || now
    const updatedAt = providedUpdatedAt || now

    // 2) UPSERT de la fila de datos (ON CONFLICT(occurrence_id, item_id), LWW-local).
    //    created_at se siembra en el primer insert; updated_at siempre.
    //
    // SERIALIZACION A TEXT: las columnas `value` y `extra_properties` son TEXT (schema.js) y la
    // UI real pasa objetos/arrays (form-reading {last,current,difference}, checkbox/multinumeric
    // [], extra_properties []). La serializacion a JSON TEXT ahora vive en BaseRepository._buildUpsert
    // (json-codec.toText), que aplica a TODO repo -> aqui ya NO hace falta el guard local (toText es
    // idempotente sobre escalares, asi que no hay doble-stringify). El read-side simetrico es
    // json-codec.parseMaybeJson (modules/occurrences.js, que re-parsea al drenar).
    const dataRow = {
      client_uuid: uuid,
      occurrence_id: occurrenceId,
      item_id: itemId,
      value,
      current_points: currentPoints,
      total_points: totalPoints,
      extra_properties: extraProperties,
      created_at: createdAt,
      updated_at: updatedAt
    }
    // FOTO STICKY (anti pérdida-silenciosa): image_ref/local_path SOLO entran al coalesce cuando se
    // proveen. Un re-save SIN foto del mismo (occurrence_id, item_id) — p.ej. setExtraProperty o una
    // re-respuesta de texto del mismo item — pasa imageRef/localPath = undefined; incluirlos NULLearía
    // la foto ya guardada (`image_ref=excluded.image_ref` en el ON CONFLICT), y al drenar el builder
    // dejaría de adjuntar el file -> el backend SOBRESCRIBIRÍA la URL ya subida = foto perdida. Para
    // BORRAR una foto explícitamente, el caller pasa image_ref: null (entra al SET y la limpia).
    if (imageRef !== undefined) dataRow.image_ref = imageRef
    if (localPath !== undefined) dataRow.local_path = localPath
    const dataStmt = this._buildUpsert(dataRow, { conflictTarget: 'occurrence_id, item_id' })

    // 3) op latest-state coalescible (occurrence.syncOccurrence), body normalizado.
    const op = 'occurrence.syncOccurrence'
    const outboxInsert = this._buildOutboxInsert({
      clientUuid: uuid,
      entity: this.entity,
      op,
      body: { occurrence_id: occurrenceId }
    })

    // 4) Escritura atomica data+outbox dentro de ESTA transaccion (sin doble-BEGIN).
    return this._saveOptimisticTx(db, { uuid, dataStmt, outboxInsert, op })
  }
}

export const answersRepository = new AnswersRepository()
